import React, { useCallback, useEffect, useState } from 'react';
import api, { getApiErrorMessage } from '../api';
import './AdminPromo.css';

const GIFTS = [
    { value: 'free', label: 'Только регистрация', hint: 'Бесплатный тариф, как при обычной регистрации' },
    { value: 'advanced', label: '«Расширенный» на срок', hint: 'Тариф включится сразу после регистрации' },
    { value: 'corporate', label: '«Корпоративный» на срок', hint: 'Для одного человека. Для сотрудников организации удобнее «Компании»' },
];
const STATUS = {
    pending: { label: 'ЖДЁТ', className: 'status-mock--pending' },
    used: { label: 'ПРИНЯТО', className: 'status-mock--active' },
    expired: { label: 'ИСТЕКЛО', className: 'status-mock--blocked' },
    revoked: { label: 'ОТОЗВАНО', className: 'status-mock--blocked' },
};
const EMPTY_FORM = { email: '', firstName: '', tier: 'free', days: 30, validDays: 14, note: '', sendEmail: true };
const dateRu = (iso) => (iso ? new Date(iso).toLocaleDateString('ru-RU') : '');

const copyText = async (text) => {
    try {
        if (navigator.clipboard && window.isSecureContext) {
            await navigator.clipboard.writeText(text);
            return true;
        }
    } catch {
        // запасной способ ниже
    }
    const area = document.createElement('textarea');
    area.value = text;
    area.style.position = 'fixed';
    area.style.opacity = '0';
    document.body.appendChild(area);
    area.select();
    let ok = false;
    try { ok = document.execCommand('copy'); } catch { ok = false; }
    document.body.removeChild(area);
    return ok;
};

function InviteForm({ onClose, onCreated }) {
    const [form, setForm] = useState(EMPTY_FORM);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [result, setResult] = useState(null);
    const [copied, setCopied] = useState(false);
    const set = (patch) => setForm((f) => ({ ...f, ...patch }));
    const gift = GIFTS.find((g) => g.value === form.tier);
    const hasEmail = Boolean(form.email.trim());

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        setError('');
        try {
            const res = await api.post('/admin/invites', {
                email: form.email.trim() || null,
                first_name: form.firstName.trim() || null,
                tier: form.tier,
                days: form.tier === 'free' ? 0 : Number(form.days) || 0,
                valid_days: Number(form.validDays) || 14,
                note: form.note.trim() || null,
                send_email: hasEmail && form.sendEmail,
            });
            setResult(res.data);
            onCreated();
        } catch (err) {
            setError(getApiErrorMessage(err, 'Не удалось создать приглашение'));
        } finally {
            setBusy(false);
        }
    };

    const copy = async () => {
        setCopied(await copyText(result.link));
        setTimeout(() => setCopied(false), 2000);
    };

    return (
        <div className="admin-modal-overlay" role="presentation" onClick={() => !busy && onClose()}>
            <div className="admin-modal admin-modal--wide promo-modal invite-modal" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
                <form onSubmit={submit}>
                    <div className="admin-modal__body">
                        <h3>{result ? 'Приглашение создано' : 'Пригласить организатора'}</h3>
                        {result ? (
                            <div className="promo-result">
                                <p>
                                    {result.email_sent
                                        ? <>Письмо со ссылкой отправлено на <b>{result.email}</b>. Ссылку можно передать и самостоятельно:</>
                                        : 'Отправьте эту ссылку новому организатору — в мессенджере или письмом:'}
                                </p>
                                <div className="analytics-link">
                                    <input readOnly value={result.link} onFocus={(e) => e.target.select()} aria-label="Ссылка-приглашение" />
                                    <button type="button" className="btn btn--secondary" onClick={copy}>{copied ? 'Скопировано ✓' : 'Копировать'}</button>
                                </div>
                                {result.email_error && <div className="admin-notice admin-notice--error" role="alert">{result.email_error}</div>}
                                <p className="promo-hint">
                                    Что даёт: {result.gift}. Ссылка одноразовая и действует до {dateRu(result.expires_at)}
                                    {result.email ? `, принять её можно только с почтой ${result.email}` : ''}.
                                </p>
                            </div>
                        ) : (
                            <>
                                <p className="promo-hint">
                                    Человек откроет ссылку, зарегистрируется в веб-кабинете (или войдёт, если аккаунт уже есть) — и приглашение применится автоматически.
                                </p>
                                <div className="promo-grid">
                                    <label className="promo-field">
                                        <span>Почта</span>
                                        <input type="email" value={form.email} onChange={(e) => set({ email: e.target.value })} placeholder="Необязательно" />
                                        <small>Если указать — ссылку примет только владелец этой почты, а регистрация будет доступна и не на .ru-адресе</small>
                                    </label>
                                    <label className="promo-field">
                                        <span>Имя</span>
                                        <input value={form.firstName} onChange={(e) => set({ firstName: e.target.value })} placeholder="Для обращения в письме" />
                                    </label>
                                </div>
                                <div className="promo-grid">
                                    <label className="promo-field">
                                        <span>Что даёт приглашение</span>
                                        <select value={form.tier} onChange={(e) => set({ tier: e.target.value })}>
                                            {GIFTS.map((g) => <option key={g.value} value={g.value}>{g.label}</option>)}
                                        </select>
                                        <small>{gift?.hint}</small>
                                    </label>
                                    {form.tier !== 'free' && (
                                        <label className="promo-field">
                                            <span>Тариф на, дней</span>
                                            <input type="number" min="1" max="3650" value={form.days} onChange={(e) => set({ days: e.target.value })} required />
                                        </label>
                                    )}
                                    <label className="promo-field">
                                        <span>Ссылка действует, дней</span>
                                        <input type="number" min="1" max="90" value={form.validDays} onChange={(e) => set({ validDays: e.target.value })} />
                                    </label>
                                </div>
                                <div className="promo-grid">
                                    <label className="promo-field">
                                        <span>Заметка</span>
                                        <input value={form.note} onChange={(e) => set({ note: e.target.value })} placeholder="Для себя: кто это и откуда" />
                                    </label>
                                </div>
                                <label className="promo-check">
                                    <input type="checkbox" checked={hasEmail && form.sendEmail} disabled={!hasEmail} onChange={(e) => set({ sendEmail: e.target.checked })} />
                                    {' '}Отправить приглашение на почту{hasEmail ? '' : ' (укажите почту)'}
                                </label>
                            </>
                        )}
                        {error && <div className="admin-notice admin-notice--error" role="alert">{error}</div>}
                    </div>
                    <div className="admin-modal__footer">
                        <button type="button" className="btn btn--secondary" onClick={onClose} disabled={busy}>{result ? 'Готово' : 'Отмена'}</button>
                        {!result && (
                            <button type="submit" className="btn btn--success" disabled={busy}>
                                {busy ? 'Создаём…' : 'Создать приглашение'}
                            </button>
                        )}
                    </div>
                </form>
            </div>
        </div>
    );
}

/** Вкладка «Приглашения»: ссылки на регистрацию в веб-кабинете, с тарифом в подарок или без. */
function AdminInvites({ openFormSignal = 0 }) {
    const [items, setItems] = useState(null);
    const [error, setError] = useState('');
    const [showForm, setShowForm] = useState(false);
    const [copiedId, setCopiedId] = useState(null);

    const load = useCallback(() => {
        api.get('/admin/invites')
            .then((res) => { setItems(res.data); setError(''); })
            .catch((err) => setError(getApiErrorMessage(err, 'Не удалось загрузить приглашения')));
    }, []);

    useEffect(() => { load(); }, [load]);
    useEffect(() => { if (openFormSignal) setShowForm(true); }, [openFormSignal]);

    const revoke = async (invite) => {
        if (!window.confirm(`Отозвать приглашение${invite.email ? ` для ${invite.email}` : ''}? Ссылка перестанет работать.`)) return;
        try {
            await api.post(`/admin/invites/${invite.id}/revoke`);
            load();
        } catch (err) {
            setError(getApiErrorMessage(err, 'Не удалось отозвать приглашение'));
        }
    };

    const copy = async (invite) => {
        if (await copyText(invite.link)) {
            setCopiedId(invite.id);
            setTimeout(() => setCopiedId(null), 2000);
        }
    };

    return (
        <section className="admin-main-card promo-admin">
            <div className="promo-admin__head">
                <h2 className="admin-main-card__title">Приглашения</h2>
                <button type="button" className="btn btn--success" onClick={() => setShowForm(true)}>+ Пригласить организатора</button>
            </div>
            <div className="promo-admin__body">
                <p className="promo-intro">
                    Ссылка ведёт на регистрацию в веб-кабинете. После входа приглашение применяется само: можно сразу подарить «Расширенный» или «Корпоративный» тариф на срок.
                    Каждая ссылка срабатывает один раз. Сотрудников организации удобнее добавлять во вкладке «Компании».
                </p>
                {error && <div className="admin-notice admin-notice--error" role="alert">{error}</div>}
                {items === null ? <p>Загрузка…</p> : items.length === 0 ? (
                    <p className="admin-modal-empty">Приглашений пока нет.</p>
                ) : (
                    <div className="admin-table-scroll">
                        <table className="admin-table promo-table">
                            <thead>
                                <tr><th>Кому</th><th>Что даёт</th><th>Создано</th><th>Действует до</th><th>Статус</th><th>Принял</th><th /></tr>
                            </thead>
                            <tbody>
                                {items.map((i) => (
                                    <tr key={i.id}>
                                        <td>
                                            <b>{i.email || 'Любая почта'}</b>
                                            {(i.first_name || i.note) && <small className="promo-sub">{[i.first_name, i.note].filter(Boolean).join(' · ')}</small>}
                                            {i.email_sent && <small className="promo-sub">письмо отправлено</small>}
                                        </td>
                                        <td>{i.gift}</td>
                                        <td>{dateRu(i.created_at)}</td>
                                        <td>{dateRu(i.expires_at)}</td>
                                        <td><span className={`status-mock ${STATUS[i.status]?.className || ''}`}>{STATUS[i.status]?.label || i.status}</span></td>
                                        <td>{i.used_by_email ? <>{i.used_by_email}<small className="promo-sub">{dateRu(i.used_at)}</small></> : '—'}</td>
                                        <td className="promo-actions">
                                            {i.status === 'pending' && (
                                                <>
                                                    <button type="button" className="btn btn--secondary" onClick={() => copy(i)}>{copiedId === i.id ? 'Скопировано ✓' : 'Ссылка'}</button>
                                                    <button type="button" className="btn btn--secondary" onClick={() => revoke(i)}>Отозвать</button>
                                                </>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
            {showForm && <InviteForm onClose={() => setShowForm(false)} onCreated={load} />}
        </section>
    );
}

export default AdminInvites;
